<!-- PatientAnalysis.vue -->
<template>
  <div
    ref="viewPatientAnalysis"
    class="content-wrapper view-patient-analysis p-0"
  >
    <!-- Controls Section -->
    <div class="summary-section mt-0 mx-2">
      <b-row class="mb-2">
        <b-col cols="12">
          <b-card class="bg-light border-0">
            <b-form-group class="mb-0">
              <b-row align-v="end">
                <b-col cols="12" md="4" class="mb-3 mb-md-0">
                  <b-form-group label="มุมมอง:" class="mb-0" label-class="mb-2">
                    <b-form-select
                      v-model="viewType"
                      :options="[
                        { value: 'month', text: 'รายเดือน' },
                        { value: 'year', text: 'รายปี' },
                      ]"
                      class="h-100"
                      :disabled="isLoading"
                    ></b-form-select>
                  </b-form-group>
                </b-col>
                <b-col cols="12" md="4" class="mb-3 mb-md-0">
                  <b-form-group
                    :label="viewType === 'month' ? 'เลือกเดือน:' : 'เลือกปี:'"
                    class="mb-0"
                    label-class="mb-2"
                  >
                    <b-form-select
                      v-if="viewType === 'month'"
                      v-model="selectedMonth"
                      :options="monthOptions"
                      class="h-100"
                      :disabled="isLoading"
                    ></b-form-select>
                    <b-form-select
                      v-else
                      v-model="selectedYear"
                      :options="yearOptions"
                      class="h-100"
                      :disabled="isLoading"
                    ></b-form-select>
                  </b-form-group>
                </b-col>
                <b-col cols="12" md="4" class="d-flex align-items-stretch">
                  <b-button
                    variant="primary"
                    @click="handleDateChange"
                    :disabled="isLoading"
                    class="w-100 d-flex align-items-center justify-content-center py-2"
                  >
                    <b-spinner small v-if="isLoading" class="mr-2"></b-spinner>
                    <span v-else>ดูข้อมูล</span>
                  </b-button>
                </b-col>
              </b-row>
            </b-form-group>
          </b-card>
        </b-col>
      </b-row>

      <!-- Summary Cards -->
      <b-overlay :show="isLoading" rounded="sm">
        <b-row>
          <b-col cols="12" md="4">
            <b-card class="summary-card mb-3 text-center">
              <div class="summary-content">
                <h3 class="text-primary mb-1">
                  {{ patientSummary.totalPatients }}
                </h3>
                <p class="text-body-secondary mb-0">จำนวนผู้ป่วยที่มีการนัดหมาย</p>
              </div>
            </b-card>
          </b-col>
          <b-col cols="12" md="4">
            <b-card class="summary-card mb-3 text-center">
              <div class="summary-content">
                <h3 class="text-success mb-1">
                  {{ patientSummary.newPatients }}
                </h3>
                <p class="text-body-secondary mb-0">ผู้ป่วยใหม่</p>
                <small class="text-muted"
                  >({{
                    patientSummary.newPatientPercentage.toFixed(1)
                  }}%)</small
                >
              </div>
            </b-card>
          </b-col>
          <b-col cols="12" md="4">
            <b-card class="summary-card mb-3 text-center">
              <div class="summary-content">
                <h3 class="text-info mb-1">
                  {{ patientSummary.returningPatients }}
                </h3>
                <p class="text-body-secondary mb-0">ผู้ป่วยเก่า</p>
              </div>
            </b-card>
          </b-col>
        </b-row>
      </b-overlay>

      <!-- AI Recommendations Button -->
      <b-row v-if="analyticsData && !isLoading" class="mt-3">
        <b-col cols="12">
          <b-card class="bg-light border-0">
            <div class="text-center">
              <b-button
                variant="outline-primary"
                size="lg"
                @click="fetchRecommendationsData"
                :disabled="isLoadingRecommendations"
                class="px-4 py-2"
              >
                <b-spinner
                  small
                  v-if="isLoadingRecommendations"
                  class="mr-2"
                ></b-spinner>
                <i v-else class="fas fa-magic mr-2"></i>
                {{
                  isLoadingRecommendations
                    ? "กำลังวิเคราะห์..."
                    : "ขอคำแนะนำจาก AI"
                }}
              </b-button>
            </div>
          </b-card>
        </b-col>
      </b-row>
    </div>

    <!-- Loading placeholder -->
    <transition name="fade">
      <b-alert
        v-if="isLoadingRecommendations"
        class="ai-loading mx-2 mt-3"
        show
        variant="info"
      >
        <div class="ai-pulse"></div>
        <span>AI กำลังวิเคราะห์ข้อมูลผู้ป่วย...</span>
      </b-alert>
    </transition>

    <!-- Main Content Tabs -->
    <b-card no-body class="mx-2">
      <b-tabs v-model="currentTab" pills card>
        <!-- Overview Tab -->
        <b-tab title="ภาพรวม" active>
          <b-card-text>
            <!-- AI Recommendations -->
            <transition-group name="recommendation" tag="div" appear>
              <b-card
                v-if="
                  !isLoadingRecommendations && currentTabRecommendations.length
                "
                key="recommendation-card"
                class="mt-2 recommendation-card"
              >
                <div class="ai-badge">
                  <span class="ai-icon">🤖</span> AI Insights
                </div>
                <h5 class="recommendation-title">
                  คำแนะนำการดึงดูดผู้ป่วยใหม่
                </h5>
                <b-list-group flush>
                  <transition-group name="recommendation-item" tag="div" appear>
                    <b-list-group-item
                      v-for="(rec, index) in currentTabRecommendations"
                      :key="rec.title"
                      class="recommendation-item"
                      :style="{ animationDelay: index * 0.1 + 's' }"
                    >
                      <div
                        class="d-flex align-items-start justify-content-between"
                      >
                        <strong>{{ rec.title }}</strong>
                        <CreateContentButton
                          :recommendation="rec"
                          @click="openContentGenerator"
                        />
                      </div>
                      <p class="mt-2 mb-2">{{ rec.description }}</p>
                      <div
                        v-if="rec.targetDemographic"
                        class="stats-box mt-2 p-2 bg-light rounded"
                      >
                        <small
                          ><strong>กลุ่มเป้าหมาย:</strong>
                          {{ rec.targetDemographic.ageGroup }}</small
                        >
                        <small
                          class="d-block"
                          v-if="rec.targetDemographic.location"
                        >
                          <strong>พื้นที่:</strong>
                          {{ rec.targetDemographic.location }}
                        </small>
                      </div>
                      <div
                        v-if="rec.strategy"
                        class="stats-box mt-2 p-2 bg-light rounded"
                      >
                        <small
                          ><strong>ช่องทาง:</strong>
                          {{ rec.strategy.channel }}</small
                        >
                        <small class="d-block" v-if="rec.strategy.content">
                          <strong>เนื้อหา:</strong> {{ rec.strategy.content }}
                        </small>
                      </div>
                      <div
                        v-if="rec.stats"
                        class="stats-box mt-2 p-2 bg-success rounded text-white"
                      >
                        <small>
                          <strong>{{ rec.stats.title }}:</strong>
                          {{ rec.stats.currentValue }}% →
                          {{ rec.stats.targetValue }}%
                        </small>
                      </div>
                    </b-list-group-item>
                  </transition-group>
                </b-list-group>
              </b-card>
            </transition-group>
            <b-row>
              <!-- Age Demographics -->
              <b-col md="6" class="mb-4">
                <h4 class="mb-3">การกระจายตามอายุ</h4>
                <div
                  v-if="
                    demographics &&
                    demographics.ageGroups &&
                    demographics.ageGroups.length
                  "
                  class="chart-container"
                >
                  <pie-chart
                    :key="`age-overview-${chartKey}`"
                    :chart-data="ageChartData"
                    :options="pieChartOptions"
                  />
                </div>
                <div v-else class="chart-placeholder">
                  <p class="text-muted">ไม่มีข้อมูล</p>
                </div>
              </b-col>

              <!-- Gender Demographics -->
              <b-col md="6" class="mb-4">
                <h4 class="mb-3">การกระจายตามเพศ</h4>
                <div
                  v-if="
                    demographics &&
                    demographics.genderDistribution &&
                    demographics.genderDistribution.length
                  "
                  class="chart-container"
                >
                  <pie-chart
                    :key="`gender-overview-${chartKey}`"
                    :chart-data="genderChartData"
                    :options="pieChartOptions"
                  />
                </div>
                <div v-else class="chart-placeholder">
                  <p class="text-muted">ไม่มีข้อมูล</p>
                </div>
              </b-col>
            </b-row>

            <!-- New vs Existing Patients Chart -->
            <div class="mb-4">
              <h4 class="mb-3">เปรียบเทียบผู้ป่วยใหม่และเก่า</h4>
              <div v-if="newVsExistingPatients" class="chart-container">
                <bar-chart
                  :key="`new-vs-existing-${chartKey}`"
                  :chart-data="newVsExistingChartData"
                  :options="barChartOptions"
                />
              </div>
              <div v-else class="chart-placeholder">
                <p class="text-muted">ไม่มีข้อมูล</p>
              </div>
            </div>

            <!-- Top Locations -->
            <div class="mb-4">
              <h4 class="mb-3">พื้นที่ที่มีผู้ป่วยมากที่สุด</h4>
              <b-table
                v-if="
                  demographics &&
                  demographics.locationDistribution &&
                  demographics.locationDistribution.length
                "
                :items="demographics.locationDistribution.slice(0, 10)"
                :fields="locationTableFields"
                striped
                hover
                responsive
              >
                <template #cell(percentage)="data">
                  {{ data.value.toFixed(1) }}%
                </template>
              </b-table>
              <div v-else class="chart-placeholder">
                <p class="text-muted">ไม่มีข้อมูล</p>
              </div>
            </div>

            <!-- Referral Sources -->
            <div class="mb-4">
              <h4 class="mb-3">แหล่งที่มาของผู้ป่วย</h4>
              <div v-if="referralSources && referralSources.length">
                <b-row>
                  <b-col md="6">
                    <div class="chart-container">
                      <pie-chart
                        :key="`referral-pie-${chartKey}`"
                        :chart-data="referralChartData"
                        :options="pieChartOptions"
                      />
                    </div>
                  </b-col>
                  <b-col md="6">
                    <b-table
                      :items="
                        referralSources
                          .filter(s => s.source !== 'Unknown')
                          .slice(0, 6)
                      "
                      :fields="referralTableFields"
                      striped
                      hover
                      responsive
                    >
                      <template #cell(percentage)="data">
                        {{ data.value.toFixed(1) }}%
                      </template>
                    </b-table>
                  </b-col>
                </b-row>
              </div>
              <div v-else class="chart-placeholder">
                <p class="text-muted">ไม่มีข้อมูล</p>
              </div>
            </div>
          </b-card-text>
        </b-tab>

        <!-- Demographics Tab -->
        <b-tab title="ประชากรศาสตร์">
          <b-card-text>
            <!-- AI Recommendations -->
            <transition-group name="recommendation" tag="div" appear>
              <b-card
                v-if="
                  !isLoadingRecommendations && currentTabRecommendations.length
                "
                key="recommendation-card"
                class="mt-2 recommendation-card"
              >
                <div class="ai-badge">
                  <span class="ai-icon">🤖</span> AI Insights
                </div>
                <h5 class="recommendation-title">คำแนะนำการรักษาผู้ป่วยเก่า</h5>
                <b-list-group flush>
                  <transition-group name="recommendation-item" tag="div" appear>
                    <b-list-group-item
                      v-for="(rec, index) in currentTabRecommendations"
                      :key="rec.title"
                      class="recommendation-item"
                      :style="{ animationDelay: index * 0.1 + 's' }"
                    >
                      <div
                        class="d-flex align-items-start justify-content-between"
                      >
                        <strong>{{ rec.title }}</strong>
                        <CreateContentButton
                          :recommendation="rec"
                          @click="openContentGenerator"
                        />
                      </div>
                      <p class="mt-2 mb-2">{{ rec.description }}</p>
                      <div
                        v-if="rec.targetSegment"
                        class="stats-box mt-2 p-2 bg-light rounded"
                      >
                        <small
                          ><strong>กลุ่มเป้าหมาย:</strong>
                          {{ rec.targetSegment }}</small
                        >
                      </div>
                      <div
                        v-if="rec.strategy"
                        class="stats-box mt-2 p-2 bg-light rounded"
                      >
                        <small
                          ><strong>วิธีการ:</strong>
                          {{ rec.strategy.approach }}</small
                        >
                        <small class="d-block" v-if="rec.strategy.timeline">
                          <strong>กรอบเวลา:</strong> {{ rec.strategy.timeline }}
                        </small>
                      </div>
                      <div
                        v-if="rec.stats"
                        class="stats-box mt-2 p-2 bg-info rounded text-white"
                      >
                        <small>
                          <strong>{{ rec.stats.title }}:</strong>
                          {{ rec.stats.currentValue }}% →
                          {{ rec.stats.targetValue }}%
                        </small>
                      </div>
                    </b-list-group-item>
                  </transition-group>
                </b-list-group>
              </b-card>
            </transition-group>
            <div v-if="demographics">
              <b-row>
                <b-col md="6" class="mb-4">
                  <h4 class="mb-3">การกระจายตามอายุ</h4>
                  <div
                    v-if="
                      demographics.ageGroups && demographics.ageGroups.length
                    "
                    class="chart-container"
                  >
                    <pie-chart
                      :key="`age-pie-${chartKey}`"
                      :chart-data="ageChartData"
                      :options="pieChartOptions"
                    />
                  </div>
                  <div v-else class="chart-placeholder">
                    <p class="text-muted">ไม่มีข้อมูล</p>
                  </div>
                </b-col>
                <b-col md="6" class="mb-4">
                  <h4 class="mb-3">การกระจายตามเพศ</h4>
                  <div
                    v-if="
                      demographics.genderDistribution &&
                      demographics.genderDistribution.length
                    "
                    class="chart-container"
                  >
                    <pie-chart
                      :key="`gender-pie-${chartKey}`"
                      :chart-data="genderChartData"
                      :options="pieChartOptions"
                    />
                  </div>
                  <div v-else class="chart-placeholder">
                    <p class="text-muted">ไม่มีข้อมูล</p>
                  </div>
                </b-col>
              </b-row>
            </div>
            <div v-else class="chart-placeholder">
              <p class="text-muted">ไม่มีข้อมูล</p>
            </div>

            <!-- New vs Existing Patients Comparison -->
            <div class="mt-4">
              <h4 class="mb-3">เปรียบเทียบผู้ป่วยใหม่และเก่า</h4>
              <div v-if="newVsExistingPatients">
                <b-row>
                  <b-col md="6">
                    <h5 class="mb-3">
                      ผู้ป่วยใหม่ ({{ newVsExistingPatients.newPatients.count }}
                      คน)
                    </h5>
                    <b-table
                      :items="
                        newVsExistingPatients.newPatients.ageDistribution
                          .filter(a => a.ageRange !== 'Unknown')
                          .slice(0, 10)
                      "
                      :fields="ageTableFields"
                      striped
                      hover
                      responsive
                      small
                    >
                      <template #cell(percentage)="data">
                        {{ data.value.toFixed(1) }}%
                      </template>
                    </b-table>
                  </b-col>
                  <b-col md="6">
                    <h5 class="mb-3">
                      ผู้ป่วยเก่า ({{
                        newVsExistingPatients.existingPatients.count
                      }}
                      คน)
                    </h5>
                    <b-table
                      :items="
                        newVsExistingPatients.existingPatients.ageDistribution
                          .filter(a => a.ageRange !== 'Unknown')
                          .slice(0, 10)
                      "
                      :fields="ageTableFields"
                      striped
                      hover
                      responsive
                      small
                    >
                      <template #cell(percentage)="data">
                        {{ data.value.toFixed(1) }}%
                      </template>
                    </b-table>
                  </b-col>
                </b-row>
              </div>
              <div v-else class="chart-placeholder">
                <p class="text-muted">ไม่มีข้อมูล</p>
              </div>
            </div>

            <!-- Location Distribution -->
            <div class="mt-4">
              <h4 class="mb-3">การกระจายตามพื้นที่</h4>
              <div
                v-if="
                  demographics &&
                  demographics.locationDistribution &&
                  demographics.locationDistribution.length
                "
              >
                <b-row>
                  <b-col md="6">
                    <div class="chart-container">
                      <bar-chart
                        :key="`location-bar-${chartKey}`"
                        :chart-data="locationBarChartData"
                        :options="horizontalBarChartOptions"
                      />
                    </div>
                  </b-col>
                  <b-col md="6">
                    <b-table
                      :items="demographics.locationDistribution"
                      :fields="locationTableFields"
                      striped
                      hover
                      responsive
                    >
                      <template #cell(percentage)="data">
                        {{ data.value.toFixed(1) }}%
                      </template>
                    </b-table>
                  </b-col>
                </b-row>
              </div>
              <div v-else class="chart-placeholder">
                <p class="text-muted">ไม่มีข้อมูล</p>
              </div>
            </div>
          </b-card-text>
        </b-tab>

        <!-- Ortho Package Tab -->
        <b-tab title="แพ็กเกจจัดฟัน">
          <b-card-text>
            <!-- AI Recommendations -->
            <transition-group name="recommendation" tag="div" appear>
              <b-card
                v-if="
                  !isLoadingRecommendations && currentTabRecommendations.length
                "
                key="recommendation-card"
                class="mt-2 recommendation-card"
              >
                <div class="ai-badge">
                  <span class="ai-icon">🤖</span> AI Insights
                </div>
                <h5 class="recommendation-title">คำแนะนำการขายแพ็กเกจ</h5>
                <b-list-group flush>
                  <transition-group name="recommendation-item" tag="div" appear>
                    <b-list-group-item
                      v-for="(rec, index) in currentTabRecommendations"
                      :key="rec.title"
                      class="recommendation-item"
                      :style="{ animationDelay: index * 0.1 + 's' }"
                    >
                      <div
                        class="d-flex align-items-start justify-content-between"
                      >
                        <strong>{{ rec.title }}</strong>
                        <CreateContentButton
                          :recommendation="rec"
                          @click="openContentGenerator"
                        />
                      </div>
                      <p class="mt-2 mb-2">{{ rec.description }}</p>
                      <div
                        v-if="rec.targetDemographic"
                        class="stats-box mt-2 p-2 bg-light rounded"
                      >
                        <small
                          ><strong>กลุ่มเป้าหมาย:</strong>
                          {{ rec.targetDemographic }}</small
                        >
                      </div>
                      <div
                        v-if="rec.packageType"
                        class="stats-box mt-2 p-2 bg-light rounded"
                      >
                        <small
                          ><strong>แพ็กเกจแนะนำ:</strong>
                          {{ rec.packageType }}</small
                        >
                      </div>
                      <div
                        v-if="rec.stats"
                        class="stats-box mt-2 p-2 bg-warning rounded"
                      >
                        <small>
                          <strong>{{ rec.stats.title }}:</strong>
                          {{ rec.stats.currentAdoption }}% →
                          {{ rec.stats.targetAdoption }}%
                        </small>
                      </div>
                    </b-list-group-item>
                  </transition-group>
                </b-list-group>
              </b-card>
            </transition-group>
            <div
              v-if="
                orthoPackageAnalysis && orthoPackageAnalysis.totalPatients > 0
              "
            >
              <b-row>
                <!-- Package Statistics -->
                <b-col md="4" class="mb-4">
                  <b-card class="text-center h-100">
                    <h5 class="text-muted">ผู้ป่วยทั้งหมด</h5>
                    <h3 class="text-primary">
                      {{ orthoPackageAnalysis.totalPatients }}
                    </h3>
                  </b-card>
                </b-col>
                <b-col md="4" class="mb-4">
                  <b-card class="text-center h-100">
                    <h5 class="text-muted">มีแพ็กเกจ</h5>
                    <h3 class="text-success">
                      {{ orthoPackageAnalysis.patientsWithPackages }}
                    </h3>
                  </b-card>
                </b-col>
                <b-col md="4" class="mb-4">
                  <b-card class="text-center h-100">
                    <h5 class="text-muted">ไม่มีแพ็กเกจ</h5>
                    <h3 class="text-warning">
                      {{ orthoPackageAnalysis.patientsWithoutPackages }}
                    </h3>
                  </b-card>
                </b-col>
              </b-row>

              <div class="mb-4">
                <h4 class="mb-3">การใช้แพ็กเกจจัดฟัน</h4>
                <div
                  v-if="
                    orthoPackageAnalysis.packageBreakdown &&
                    orthoPackageAnalysis.packageBreakdown.length
                  "
                  class="chart-container"
                >
                  <pie-chart
                    :key="`package-pie-${chartKey}`"
                    :chart-data="packageChartData"
                    :options="pieChartOptions"
                  />
                </div>
                <div v-else class="chart-placeholder">
                  <p class="text-muted">ไม่มีข้อมูล</p>
                </div>
              </div>

              <div class="mt-4">
                <h4 class="mb-3">รายละเอียดแพ็กเกจ</h4>
                <div
                  v-if="
                    orthoPackageAnalysis.packageBreakdown &&
                    orthoPackageAnalysis.packageBreakdown.length
                  "
                >
                  <b-table
                    :items="orthoPackageAnalysis.packageBreakdown"
                    :fields="packageTableFields"
                    striped
                    hover
                    responsive
                  >
                    <template #cell(percentage)="data">
                      {{ data.value.toFixed(1) }}%
                    </template>
                  </b-table>
                </div>
                <div v-else class="chart-placeholder">
                  <p class="text-muted">ไม่มีข้อมูล</p>
                </div>
              </div>
            </div>
            <div v-else class="chart-placeholder">
              <p class="text-muted">ไม่มีข้อมูล</p>
            </div>
          </b-card-text>
        </b-tab>

        <!-- Patient Segmentation Tab -->
        <b-tab title="กลุ่มลูกค้า">
          <b-card-text>
            <div
              v-if="
                patientSegmentation &&
                patientSegmentation.highValuePatientProfiles
              "
            >
              <h4 class="mb-3">การแบ่งกลุ่มตามมูลค่าการใช้จ่าย</h4>

              <!-- Segment Overview -->
              <b-row class="mb-4">
                <!-- First row: 3 cards -->
                <b-col
                  md="6"
                  lg="4"
                  class="mb-3"
                  v-for="(
                    segment, index
                  ) in patientSegmentation.highValuePatientProfiles.segments.slice(
                    0,
                    3
                  )"
                  :key="index"
                >
                  <b-card
                    class="text-center h-100"
                    :bg-variant="getSegmentVariant(index)"
                    text-variant="white"
                  >
                    <h6>{{ segment.segmentName }}</h6>
                    <h4>{{ segment.patientCount }} คน</h4>
                    <small
                      >{{ segment.percentage.toFixed(1) }}%
                      ของผู้ป่วยที่จ่าย</small
                    >
                    <hr class="my-2" />
                    <small
                      >เฉลี่ย:
                      {{ formatCurrency(segment.averageSpending) }}</small
                    >
                    <small class="d-block"
                      >รวม: {{ formatCurrency(segment.totalSpending) }}</small
                    >
                    <small class="d-block"
                      >ช่วง: {{ formatCurrency(segment.spendingRange.min) }} -
                      {{ formatCurrency(segment.spendingRange.max) }}</small
                    >
                  </b-card>
                </b-col>
              </b-row>
              <!-- Second row: remaining cards centered -->
              <b-row
                class="mb-4"
                v-if="
                  patientSegmentation.highValuePatientProfiles.segments.length >
                  3
                "
              >
                <b-col class="d-flex justify-content-center">
                  <div
                    class="d-flex flex-wrap justify-content-center"
                    style="gap: 1rem"
                  >
                    <div
                      v-for="(
                        segment, index
                      ) in patientSegmentation.highValuePatientProfiles.segments.slice(
                        3
                      )"
                      :key="index + 3"
                      style="width: 280px"
                    >
                      <b-card
                        class="text-center h-100"
                        :bg-variant="getSegmentVariant(index + 3)"
                        text-variant="white"
                      >
                        <h6>{{ segment.segmentName }}</h6>
                        <h4>{{ segment.patientCount }} คน</h4>
                        <small
                          >{{ segment.percentage.toFixed(1) }}%
                          ของผู้ป่วยที่จ่าย</small
                        >
                        <hr class="my-2" />
                        <small
                          >เฉลี่ย:
                          {{ formatCurrency(segment.averageSpending) }}</small
                        >
                        <small class="d-block"
                          >รวม:
                          {{ formatCurrency(segment.totalSpending) }}</small
                        >
                        <small class="d-block"
                          >ช่วง:
                          {{ formatCurrency(segment.spendingRange.min) }} -
                          {{ formatCurrency(segment.spendingRange.max) }}</small
                        >
                      </b-card>
                    </div>
                  </div>
                </b-col>
              </b-row>

              <!-- Segment Charts -->
              <b-row class="mb-4">
                <b-col md="12">
                  <h4 class="mb-3">รายได้ตามกลุ่ม</h4>
                  <div
                    v-if="
                      patientSegmentation.highValuePatientProfiles.segments &&
                      patientSegmentation.highValuePatientProfiles.segments
                        .length
                    "
                    class="chart-container"
                  >
                    <bar-chart
                      :key="`segment-revenue-bar-${chartKey}`"
                      :chart-data="segmentRevenueChartData"
                      :options="barChartOptions"
                    />
                  </div>
                  <div v-else class="chart-placeholder">
                    <p class="text-muted">ไม่มีข้อมูล</p>
                  </div>
                </b-col>
              </b-row>

              <!-- Spending Statistics -->
              <h4 class="mb-3">สถิติการใช้จ่าย</h4>
              <b-row class="mb-4">
                <b-col md="3">
                  <b-card class="text-center">
                    <h6 class="text-muted">ผู้ป่วยที่มีการจ่าย</h6>
                    <h4>
                      {{
                        patientSegmentation.highValuePatientProfiles
                          .totalPatients
                      }}
                    </h4>
                  </b-card>
                </b-col>
                <b-col md="3">
                  <b-card class="text-center">
                    <h6 class="text-muted">การใช้จ่ายเฉลี่ย</h6>
                    <h4>
                      {{
                        formatCurrency(
                          patientSegmentation.highValuePatientProfiles.insights
                            .averageSpending
                        )
                      }}
                    </h4>
                  </b-card>
                </b-col>
                <b-col md="3">
                  <b-card class="text-center">
                    <h6 class="text-muted">ค่ามัธยฐาน</h6>
                    <h4>
                      {{
                        formatCurrency(
                          patientSegmentation.highValuePatientProfiles.insights
                            .medianSpending
                        )
                      }}
                    </h4>
                  </b-card>
                </b-col>
                <b-col md="3">
                  <b-card class="text-center">
                    <h6 class="text-muted">รายได้รวม</h6>
                    <h4>
                      {{
                        formatCurrency(
                          patientSegmentation.highValuePatientProfiles.insights
                            .totalRevenue
                        )
                      }}
                    </h4>
                  </b-card>
                </b-col>
              </b-row>

              <!-- Detailed Segment Analysis -->
              <div class="mt-4">
                <h4 class="mb-3">รายละเอียดแต่ละกลุ่ม</h4>
                <b-card no-body>
                  <b-tabs
                    pills
                    card
                    v-model="activeSegmentTab"
                    @input="onSegmentTabChange"
                  >
                    <b-tab
                      v-for="(segment, index) in patientSegmentation
                        .highValuePatientProfiles.segments"
                      :key="`segment-${index}`"
                      :title="segment.segmentName"
                    >
                      <b-card-text>
                        <div class="segment-details">
                          <!-- Segment Summary Info -->
                          <b-row class="mb-4">
                            <b-col md="12">
                              <b-alert show variant="info" class="mb-3">
                                <strong>{{ segment.segmentName }}</strong
                                ><br />
                                จำนวนผู้ป่วย: {{ segment.patientCount }} คน ({{
                                  segment.percentage.toFixed(1)
                                }}%)<br />
                                การใช้จ่ายเฉลี่ย:
                                {{ formatCurrency(segment.averageSpending)
                                }}<br />
                                การใช้จ่ายรวม:
                                {{ formatCurrency(segment.totalSpending)
                                }}<br />
                                ช่วงการใช้จ่าย:
                                {{ formatCurrency(segment.spendingRange.min) }}
                                -
                                {{ formatCurrency(segment.spendingRange.max) }}
                              </b-alert>
                            </b-col>
                          </b-row>

                          <b-row>
                            <b-col md="4" class="mb-4">
                              <h5 class="mb-3">การกระจายตามอายุ</h5>
                              <div
                                v-if="
                                  segment.demographics &&
                                  segment.demographics.ageGroups
                                "
                              >
                                <b-table
                                  :items="
                                    segment.demographics.ageGroups
                                      .filter(a => a.ageRange !== 'Unknown')
                                      .slice(0, 8)
                                  "
                                  :fields="[
                                    { key: 'ageRange', label: 'กลุ่มอายุ' },
                                    { key: 'count', label: 'จำนวน' },
                                    { key: 'percentage', label: '%' },
                                  ]"
                                  striped
                                  small
                                  responsive
                                >
                                  <template #cell(percentage)="data">
                                    {{ data.value.toFixed(1) }}%
                                  </template>
                                </b-table>
                              </div>
                              <p v-else class="text-muted">
                                ไม่มีข้อมูลกลุ่มอายุ
                              </p>
                            </b-col>
                            <b-col md="4" class="mb-4">
                              <h5 class="mb-3">การกระจายตามเพศ</h5>
                              <div
                                v-if="
                                  segment.demographics &&
                                  segment.demographics.genderDistribution
                                "
                              >
                                <b-table
                                  :items="
                                    segment.demographics.genderDistribution.filter(
                                      g => g.gender !== 'Unknown'
                                    )
                                  "
                                  :fields="[
                                    { key: 'gender', label: 'เพศ' },
                                    { key: 'count', label: 'จำนวน' },
                                    { key: 'percentage', label: '%' },
                                  ]"
                                  striped
                                  small
                                  responsive
                                >
                                  <template #cell(percentage)="data">
                                    {{ data.value.toFixed(1) }}%
                                  </template>
                                </b-table>
                              </div>
                              <p v-else class="text-muted">ไม่มีข้อมูลเพศ</p>
                            </b-col>
                            <b-col md="4" class="mb-4">
                              <h5 class="mb-3">พื้นที่หลัก</h5>
                              <div
                                v-if="
                                  segment.demographics &&
                                  segment.demographics.topLocations &&
                                  segment.demographics.topLocations.length
                                "
                              >
                                <b-table
                                  :items="
                                    segment.demographics.topLocations.slice(
                                      0,
                                      8
                                    )
                                  "
                                  :fields="[
                                    { key: 'district', label: 'เขต/อำเภอ' },
                                    { key: 'count', label: 'จำนวน' },
                                    { key: 'percentage', label: '%' },
                                  ]"
                                  striped
                                  small
                                  responsive
                                >
                                  <template #cell(percentage)="data">
                                    {{ data.value.toFixed(1) }}%
                                  </template>
                                </b-table>
                              </div>
                              <p v-else class="text-muted">
                                ไม่มีข้อมูลพื้นที่
                              </p>
                            </b-col>
                          </b-row>
                        </div>
                      </b-card-text>
                    </b-tab>
                  </b-tabs>
                </b-card>
              </div>
            </div>
            <p v-else>ไม่มีข้อมูลการแบ่งกลุ่มลูกค้า</p>
          </b-card-text>
        </b-tab>

        <!-- Geographic Analysis Tab -->
        <b-tab title="การวิเคราะห์พื้นที่">
          <b-card-text>
            <!-- AI Recommendations -->
            <transition-group name="recommendation" tag="div" appear>
              <b-card
                v-if="
                  !isLoadingRecommendations && currentTabRecommendations.length
                "
                key="recommendation-card"
                class="mt-2 recommendation-card"
              >
                <div class="ai-badge">
                  <span class="ai-icon">🤖</span> AI Insights
                </div>
                <h5 class="recommendation-title">คำแนะนำการตลาดตามพื้นที่</h5>
                <b-list-group flush>
                  <transition-group name="recommendation-item" tag="div" appear>
                    <b-list-group-item
                      v-for="(rec, index) in currentTabRecommendations"
                      :key="rec.title"
                      class="recommendation-item"
                      :style="{ animationDelay: index * 0.1 + 's' }"
                    >
                      <div
                        class="d-flex align-items-start justify-content-between"
                      >
                        <strong>{{ rec.title }}</strong>
                        <CreateContentButton
                          :recommendation="rec"
                          @click="openContentGenerator"
                        />
                      </div>
                      <p class="mt-2 mb-2">{{ rec.description }}</p>
                      <div
                        v-if="rec.targetLocation"
                        class="stats-box mt-2 p-2 bg-light rounded"
                      >
                        <small
                          ><strong>จังหวัด:</strong>
                          {{ rec.targetLocation.province }}</small
                        >
                        <small class="d-block"
                          ><strong>เขต/อำเภอ:</strong>
                          {{ rec.targetLocation.district }}</small
                        >
                      </div>
                      <div
                        v-if="rec.stats"
                        class="stats-box mt-2 p-2 bg-primary rounded text-white"
                      >
                        <small>
                          <strong>{{ rec.stats.title }}:</strong>
                          {{ rec.stats.currentPatients }} คน ({{
                            rec.stats.potential
                          }})
                        </small>
                      </div>
                    </b-list-group-item>
                  </transition-group>
                </b-list-group>
              </b-card>
            </transition-group>
            <div
              v-if="
                patientSegmentation &&
                patientSegmentation.geographicSpendingAnalysis
              "
            >
              <!-- Top Spending Areas Chart -->
              <div class="mb-4">
                <h4 class="mb-3">พื้นที่ที่มีการใช้จ่ายสูงสุด (Top 10)</h4>
                <div
                  v-if="
                    patientSegmentation.geographicSpendingAnalysis
                      .topSpendingAreas &&
                    patientSegmentation.geographicSpendingAnalysis
                      .topSpendingAreas.length
                  "
                  class="chart-container"
                >
                  <bar-chart
                    :key="`top-areas-bar-${chartKey}`"
                    :chart-data="topSpendingAreasChartData"
                    :options="horizontalBarChartOptions"
                  />
                </div>
                <div v-else class="chart-placeholder">
                  <p class="text-muted">ไม่มีข้อมูล</p>
                </div>
              </div>

              <!-- Province Analysis Chart -->
              <div class="mb-4">
                <h4 class="mb-3">การใช้จ่ายตามจังหวัด (Top 8)</h4>
                <div
                  v-if="
                    patientSegmentation.geographicSpendingAnalysis
                      .provinceAnalysis &&
                    patientSegmentation.geographicSpendingAnalysis
                      .provinceAnalysis.length
                  "
                >
                  <b-row>
                    <b-col md="6">
                      <div class="chart-container">
                        <pie-chart
                          :key="`province-pie-${chartKey}`"
                          :chart-data="provinceSpendingChartData"
                          :options="pieChartOptions"
                        />
                      </div>
                    </b-col>
                    <b-col md="6">
                      <div class="chart-container">
                        <bar-chart
                          :key="`province-bar-${chartKey}`"
                          :chart-data="provinceSpendingBarChartData"
                          :options="barChartOptions"
                        />
                      </div>
                    </b-col>
                  </b-row>
                </div>
                <div v-else class="chart-placeholder">
                  <p class="text-muted">ไม่มีข้อมูล</p>
                </div>
              </div>

              <!-- Detailed Tables -->
              <h4 class="mb-3">พื้นที่ที่มีการใช้จ่ายสูงสุด (รายละเอียด)</h4>
              <div
                v-if="
                  patientSegmentation.geographicSpendingAnalysis
                    .topSpendingAreas &&
                  patientSegmentation.geographicSpendingAnalysis
                    .topSpendingAreas.length
                "
              >
                <b-table
                  :items="
                    patientSegmentation.geographicSpendingAnalysis.topSpendingAreas.slice(
                      0,
                      15
                    )
                  "
                  :fields="geographicFields"
                  striped
                  hover
                  responsive
                >
                  <template #cell(averageSpendingPerPatient)="data">
                    {{ formatCurrency(data.value) }}
                  </template>
                  <template #cell(totalSpending)="data">
                    {{ formatCurrency(data.value) }}
                  </template>
                </b-table>
              </div>
              <div v-else class="chart-placeholder">
                <p class="text-muted">ไม่มีข้อมูล</p>
              </div>

              <h4 class="mb-3 mt-4">สรุปตามจังหวัด</h4>
              <div
                v-if="
                  patientSegmentation.geographicSpendingAnalysis
                    .provinceAnalysis &&
                  patientSegmentation.geographicSpendingAnalysis
                    .provinceAnalysis.length
                "
              >
                <b-table
                  :items="
                    patientSegmentation.geographicSpendingAnalysis.provinceAnalysis.slice(
                      0,
                      10
                    )
                  "
                  :fields="provinceFields"
                  striped
                  hover
                  responsive
                >
                  <template #cell(averageSpendingPerPatient)="data">
                    {{ formatCurrency(data.value) }}
                  </template>
                  <template #cell(totalSpending)="data">
                    {{ formatCurrency(data.value) }}
                  </template>
                </b-table>
              </div>
              <div v-else class="chart-placeholder">
                <p class="text-muted">ไม่มีข้อมูล</p>
              </div>
            </div>
            <p v-else>ไม่มีข้อมูลการวิเคราะห์พื้นที่</p>
          </b-card-text>
        </b-tab>
      </b-tabs>
    </b-card>

    <!-- Content Generator Modal -->
    <ContentGenerator
      ref="contentGenerator"
      :recommendation="selectedRecommendation || {}"
      :clinicUrl="$route.params.clinicUrl"
      :branchUrl="$route.params.branchUrl"
    />
  </div>
</template>

<script>
import PieChart from "@/components/chart/PieChart.vue";
import BarChart from "@/components/chart/BarChart.vue";
import ContentGenerator from "@/components/modal/ContentGenerator.vue";
import CreateContentButton from "@/components/buttons/CreateContentButton.vue";
import moment from "moment";
import { mapActions } from "vuex";

export default {
  name: "PatientAnalysis",

  components: {
    PieChart,
    BarChart,
    ContentGenerator,
    CreateContentButton,
  },

  watch: {
    currentTab: {
      handler() {
        this.$nextTick(() => {
          this.forceChartRerender();
        });
      },
    },
    viewType: {
      handler(newValue) {
        this.isLoading = false;
        this.analyticsData = null;
        this.dateRange = null;
        this.chartKey = 0;

        this.resetData();

        if (newValue === "month") {
          this.selectedMonth = moment().format("YYYY-MM");
        } else {
          this.selectedYear = moment().format("YYYY");
        }
      },
    },
    patientSegmentation: {
      handler(newValue) {
        if (
          newValue &&
          newValue.highValuePatientProfiles &&
          newValue.highValuePatientProfiles.segments
        ) {
          // Reset to first tab when new data arrives
          this.activeSegmentTab = 0;
        }
      },
      immediate: true,
    },
    activeSegmentTab: {
      handler() {
        this.$nextTick(() => {
          this.forceChartRerender();
        });
      },
    },
  },

  data() {
    return {
      isLoading: false,
      isLoadingRecommendations: false,
      analyticsData: null,
      viewType: "month",
      selectedYear: moment().format("YYYY"),
      yearOptions: this.getLastThreeYears(),
      selectedMonth: moment().format("YYYY-MM"),
      monthOptions: this.getLastTwentyFourMonth(),
      dateRange: null,
      currentTab: 0,
      chartKey: 0,
      activeSegmentTab: 0,

      // Recommendations data
      recommendations: {
        acquisition: [],
        retention: [],
        geographic: [],
        referral: [],
        packages: [],
      },

      // Selected recommendation for content generator
      selectedRecommendation: null,

      // Data properties
      patientSummary: {
        totalPatients: 0,
        newPatients: 0,
        returningPatients: 0,
        newPatientPercentage: 0,
      },
      demographics: null,
      referralSources: [],
      orthoPackageAnalysis: {
        totalPatients: 0,
        patientsWithPackages: 0,
        patientsWithoutPackages: 0,
        packageAdoptionRate: 0,
        packageBreakdown: [],
      },
      patientSegmentation: null,
      treatmentAffinityMapping: null,
      newVsExistingPatients: null,

      // Chart colors
      chartColors: [
        "#8B5CF6",
        "#06B6D4",
        "#10B981",
        "#F59E0B",
        "#EF4444",
        "#EC4899",
        "#6366F1",
        "#84CC16",
        "#F97316",
        "#64748B",
        "#0EA5E9",
        "#F87171",
        "#A78BFA",
        "#34D399",
        "#FBBF24",
      ],

      // Table field definitions
      ageTableFields: [
        { key: "ageRange", label: "กลุ่มอายุ" },
        { key: "count", label: "จำนวน" },
        { key: "percentage", label: "%" },
      ],
      genderTableFields: [
        { key: "gender", label: "เพศ" },
        { key: "count", label: "จำนวน" },
        { key: "percentage", label: "%" },
      ],
      referralTableFields: [
        { key: "source", label: "แหล่งที่มา" },
        { key: "count", label: "จำนวน" },
        { key: "percentage", label: "%" },
      ],
      packageTableFields: [
        { key: "packageName", label: "แพ็กเกจ" },
        { key: "count", label: "จำนวนผู้ป่วย" },
        { key: "percentage", label: "%" },
      ],
      locationTableFields: [
        { key: "province", label: "จังหวัด" },
        { key: "district", label: "เขต/อำเภอ" },
        { key: "count", label: "จำนวน" },
        { key: "percentage", label: "%" },
      ],
      geographicFields: [
        { key: "province", label: "จังหวัด" },
        { key: "district", label: "เขต/อำเภอ" },
        { key: "patientCount", label: "ผู้ป่วย" },
        { key: "totalSpending", label: "ยอดรวม" },
        { key: "averageSpendingPerPatient", label: "เฉลี่ยต่อคน" },
      ],
      provinceFields: [
        { key: "province", label: "จังหวัด" },
        { key: "patientCount", label: "ผู้ป่วย" },
        { key: "totalSpending", label: "ยอดรวม" },
        { key: "averageSpendingPerPatient", label: "เฉลี่ยต่อคน" },
      ],
    };
  },

  computed: {
    startDt() {
      if (this.viewType === "year") {
        return moment(this.selectedYear).startOf("year").format();
      }
      return this.selectedMonth
        ? moment(this.selectedMonth).startOf("month").format()
        : "";
    },
    endDt() {
      if (this.viewType === "year") {
        return moment(this.selectedYear).endOf("year").format();
      }
      return this.selectedMonth
        ? moment(this.selectedMonth).endOf("month").format()
        : "";
    },

    ageChartData() {
      if (!this.demographics || !this.demographics.ageGroups)
        return { labels: [], datasets: [] };

      const filteredData = this.demographics.ageGroups.filter(
        item => item.ageRange !== "Unknown"
      );

      return {
        labels: filteredData.map(item => item.ageRange),
        datasets: [
          {
            backgroundColor: this.chartColors.slice(0, filteredData.length),
            data: filteredData.map(item => item.count),
          },
        ],
      };
    },

    ageBarChartData() {
      if (!this.demographics || !this.demographics.ageGroups)
        return { labels: [], datasets: [] };

      const filteredData = this.demographics.ageGroups.filter(
        item => item.ageRange !== "Unknown"
      );

      return {
        labels: filteredData.map(item => item.ageRange),
        datasets: [
          {
            label: "จำนวนผู้ป่วย",
            backgroundColor: this.chartColors[0],
            borderColor: this.chartColors[0],
            data: filteredData.map(item => item.count),
          },
        ],
      };
    },

    genderChartData() {
      if (!this.demographics || !this.demographics.genderDistribution)
        return { labels: [], datasets: [] };

      const filteredData = this.demographics.genderDistribution.filter(
        item => item.gender !== "Unknown"
      );

      return {
        labels: filteredData.map(item => item.gender),
        datasets: [
          {
            backgroundColor: ["#06B6D4", "#EC4899", "#F59E0B"],
            data: filteredData.map(item => item.count),
          },
        ],
      };
    },

    genderBarChartData() {
      if (!this.demographics || !this.demographics.genderDistribution)
        return { labels: [], datasets: [] };

      const filteredData = this.demographics.genderDistribution.filter(
        item => item.gender !== "Unknown"
      );

      return {
        labels: filteredData.map(item => item.gender),
        datasets: [
          {
            label: "จำนวนผู้ป่วย",
            backgroundColor: ["#06B6D4", "#EC4899", "#F59E0B"],
            data: filteredData.map(item => item.count),
          },
        ],
      };
    },

    newVsExistingChartData() {
      if (!this.newVsExistingPatients) return { labels: [], datasets: [] };

      const newData =
        this.newVsExistingPatients.newPatients.ageDistribution.filter(
          item => item.ageRange !== "Unknown"
        );
      const existingData =
        this.newVsExistingPatients.existingPatients.ageDistribution.filter(
          item => item.ageRange !== "Unknown"
        );

      return {
        labels: newData.map(item => item.ageRange),
        datasets: [
          {
            label: "ผู้ป่วยใหม่",
            backgroundColor: "#10B981",
            data: newData.map(item => item.count),
          },
          {
            label: "ผู้ป่วยเก่า",
            backgroundColor: "#06B6D4",
            data: existingData.map(item => item.count),
          },
        ],
      };
    },

    referralChartData() {
      if (!this.referralSources.length) return { labels: [], datasets: [] };

      const filteredData = this.referralSources.filter(
        item => item.source !== "Unknown"
      );

      return {
        labels: filteredData.map(item => item.source),
        datasets: [
          {
            backgroundColor: this.chartColors.slice(0, filteredData.length),
            data: filteredData.map(item => item.count),
          },
        ],
      };
    },

    packageChartData() {
      if (
        !this.orthoPackageAnalysis ||
        !this.orthoPackageAnalysis.packageBreakdown ||
        !this.orthoPackageAnalysis.packageBreakdown.length
      )
        return { labels: [], datasets: [] };

      return {
        labels: this.orthoPackageAnalysis.packageBreakdown.map(
          item => item.packageName
        ),
        datasets: [
          {
            backgroundColor: this.chartColors.slice(
              0,
              this.orthoPackageAnalysis.packageBreakdown.length
            ),
            data: this.orthoPackageAnalysis.packageBreakdown.map(
              item => item.count
            ),
          },
        ],
      };
    },

    packageBarChartData() {
      if (
        !this.orthoPackageAnalysis ||
        !this.orthoPackageAnalysis.packageBreakdown ||
        !this.orthoPackageAnalysis.packageBreakdown.length
      )
        return { labels: [], datasets: [] };

      return {
        labels: this.orthoPackageAnalysis.packageBreakdown.map(
          item => item.packageName
        ),
        datasets: [
          {
            label: "จำนวนผู้ป่วย",
            backgroundColor: this.chartColors[0],
            data: this.orthoPackageAnalysis.packageBreakdown.map(
              item => item.count
            ),
          },
        ],
      };
    },

    packageAdoptionChartData() {
      if (!this.orthoPackageAnalysis) return { labels: [], datasets: [] };

      return {
        labels: ["มีแพ็กเกจ", "ไม่มีแพ็กเกจ"],
        datasets: [
          {
            backgroundColor: ["#10B981", "#F59E0B"],
            data: [
              this.orthoPackageAnalysis.patientsWithPackages,
              this.orthoPackageAnalysis.patientsWithoutPackages,
            ],
          },
        ],
      };
    },

    segmentCountChartData() {
      if (
        !this.patientSegmentation ||
        !this.patientSegmentation.highValuePatientProfiles
      )
        return { labels: [], datasets: [] };

      const segments =
        this.patientSegmentation.highValuePatientProfiles.segments;

      return {
        labels: segments.map(segment => segment.segmentName),
        datasets: [
          {
            backgroundColor: ["#EF4444", "#F59E0B", "#06B6D4", "#64748B"],
            data: segments.map(segment => segment.patientCount),
          },
        ],
      };
    },

    segmentRevenueChartData() {
      if (
        !this.patientSegmentation ||
        !this.patientSegmentation.highValuePatientProfiles
      )
        return { labels: [], datasets: [] };

      const segments =
        this.patientSegmentation.highValuePatientProfiles.segments;

      return {
        labels: segments.map(segment => segment.segmentName),
        datasets: [
          {
            label: "รายได้รวม (บาท)",
            backgroundColor: ["#EF4444", "#F59E0B", "#06B6D4", "#64748B"],
            data: segments.map(segment => segment.totalSpending),
          },
        ],
      };
    },

    locationBarChartData() {
      if (!this.demographics || !this.demographics.locationDistribution)
        return { labels: [], datasets: [] };

      const topLocations = this.demographics.locationDistribution.slice(0, 10);

      return {
        labels: topLocations.map(item => `${item.district}`),
        datasets: [
          {
            label: "จำนวนผู้ป่วย",
            backgroundColor: this.chartColors[0],
            data: topLocations.map(item => item.count),
          },
        ],
      };
    },

    topSpendingAreasChartData() {
      if (
        !this.patientSegmentation ||
        !this.patientSegmentation.geographicSpendingAnalysis
      )
        return { labels: [], datasets: [] };

      const topAreas =
        this.patientSegmentation.geographicSpendingAnalysis.topSpendingAreas.slice(
          0,
          10
        );

      return {
        labels: topAreas.map(item => `${item.district}`),
        datasets: [
          {
            label: "การใช้จ่าย (บาท)",
            backgroundColor: this.chartColors[2],
            data: topAreas.map(item => item.totalSpending),
          },
        ],
      };
    },

    provinceSpendingChartData() {
      if (
        !this.patientSegmentation ||
        !this.patientSegmentation.geographicSpendingAnalysis
      )
        return { labels: [], datasets: [] };

      const provinces =
        this.patientSegmentation.geographicSpendingAnalysis.provinceAnalysis.slice(
          0,
          8
        );

      return {
        labels: provinces.map(item => item.province),
        datasets: [
          {
            backgroundColor: this.chartColors.slice(0, provinces.length),
            data: provinces.map(item => item.totalSpending),
          },
        ],
      };
    },

    provinceSpendingBarChartData() {
      if (
        !this.patientSegmentation ||
        !this.patientSegmentation.geographicSpendingAnalysis
      )
        return { labels: [], datasets: [] };

      const provinces =
        this.patientSegmentation.geographicSpendingAnalysis.provinceAnalysis.slice(
          0,
          8
        );

      return {
        labels: provinces.map(item => item.province),
        datasets: [
          {
            label: "การใช้จ่าย (บาท)",
            backgroundColor: this.chartColors[3],
            data: provinces.map(item => item.totalSpending),
          },
        ],
      };
    },

    // Computed properties for segment chart data to improve reactivity
    segmentAgeChartDataForActiveTab() {
      if (
        !this.patientSegmentation ||
        !this.patientSegmentation.highValuePatientProfiles ||
        !this.patientSegmentation.highValuePatientProfiles.segments ||
        this.activeSegmentTab >=
          this.patientSegmentation.highValuePatientProfiles.segments.length
      ) {
        return { labels: [], datasets: [] };
      }

      const segment =
        this.patientSegmentation.highValuePatientProfiles.segments[
          this.activeSegmentTab
        ];
      const chartData = this.getSegmentAgeChartData(segment);
      return chartData;
    },

    segmentGenderChartDataForActiveTab() {
      if (
        !this.patientSegmentation ||
        !this.patientSegmentation.highValuePatientProfiles ||
        !this.patientSegmentation.highValuePatientProfiles.segments ||
        this.activeSegmentTab >=
          this.patientSegmentation.highValuePatientProfiles.segments.length
      ) {
        return { labels: [], datasets: [] };
      }

      const segment =
        this.patientSegmentation.highValuePatientProfiles.segments[
          this.activeSegmentTab
        ];
      const chartData = this.getSegmentGenderChartData(segment);
      return chartData;
    },

    pieChartOptions() {
      return {
        responsive: true,
        maintainAspectRatio: false,
        legend: {
          position: "right",
          labels: {
            padding: 20,
            boxWidth: 15,
          },
        },
        tooltips: {
          callbacks: {
            label: (tooltipItem, data) => {
              const dataset = data.datasets[tooltipItem.datasetIndex];
              const total = dataset.data.reduce((acc, val) => acc + val, 0);
              const value = dataset.data[tooltipItem.index];
              const percentage = ((value / total) * 100).toFixed(1);
              const label = data.labels[tooltipItem.index];
              return `${label}: ${value} (${percentage}%)`;
            },
          },
        },
      };
    },

    smallPieChartOptions() {
      return {
        responsive: true,
        maintainAspectRatio: false,
        legend: {
          position: "bottom",
          labels: {
            padding: 10,
            boxWidth: 12,
            fontSize: 10,
          },
        },
        tooltips: {
          callbacks: {
            label: (tooltipItem, data) => {
              const dataset = data.datasets[tooltipItem.datasetIndex];
              const total = dataset.data.reduce((acc, val) => acc + val, 0);
              const value = dataset.data[tooltipItem.index];
              const percentage = ((value / total) * 100).toFixed(1);
              const label = data.labels[tooltipItem.index];
              return `${label}: ${value} (${percentage}%)`;
            },
          },
        },
      };
    },

    barChartOptions() {
      return {
        responsive: true,
        maintainAspectRatio: false,
        legend: {
          display: true,
          position: "top",
        },
        scales: {
          y: {
            beginAtZero: true,
          },
        },
        tooltips: {
          mode: "index",
          intersect: false,
        },
      };
    },

    horizontalBarChartOptions() {
      return {
        responsive: true,
        maintainAspectRatio: false,
        indexAxis: "y",
        legend: {
          display: true,
          position: "top",
        },
        scales: {
          x: {
            beginAtZero: true,
          },
        },
        tooltips: {
          mode: "index",
          intersect: false,
        },
      };
    },

    currentTabRecommendations() {
      const tabMapping = {
        0: "acquisition", // Overview - focus on acquisition
        1: "retention", // Demographics - focus on retention
        2: "packages", // Ortho Package - focus on packages
        3: "retention", // Patient Segmentation - focus on retention
        4: "geographic", // Geographic Analysis - focus on geographic
      };
      const currentTabType = tabMapping[this.currentTab] || "acquisition";
      return this.recommendations[currentTabType] || [];
    },
  },

  methods: {
    ...mapActions({
      fetchPatientAnalyticsAction: "moduleAiAnalysis/fetchPatientAnalytics",
      fetchPatientRecommendationsAction:
        "moduleAiAnalysis/fetchPatientRecommendations",
      generateSocialContentAction: "moduleAiAnalysis/generateSocialContent",
    }),

    getLastTwentyFourMonth() {
      moment.locale("th");
      const options = [];
      for (let i = 0; i < 24; i++) {
        const date = moment().subtract(i, "months");
        options.unshift({
          value: date.format("YYYY-MM"),
          text: date.format("MMMM YYYY"),
        });
      }
      return options;
    },

    getLastThreeYears() {
      const years = [];
      const currentYear = moment().year();
      for (let i = 0; i < 3; i++) {
        const year = currentYear - i;
        years.unshift({
          value: year.toString(),
          text: `ปี ${year + 543}`,
        });
      }
      return years;
    },

    async handleDateChange() {
      try {
        this.isLoading = true;
        this.resetData();
        await this.fetchPatientAnalytics();
      } catch (error) {
        console.error("Error in handleDateChange:", error);
      } finally {
        this.isLoading = false;
      }
    },

    async fetchPatientAnalytics() {
      try {
        const response = await this.fetchPatientAnalyticsAction({
          clinicUrl: this.$route.params.clinicUrl,
          branchUrl: this.$route.params.branchUrl,
          startDt: this.startDt,
          endDt: this.endDt,
        });

        if (response?.data?.patientAnalysisResult) {
          this.analyticsData = response.data.patientAnalysisResult;
          this.processPatientAnalysisData(response.data.patientAnalysisResult);
          this.forceChartRerender();
        }
      } catch (error) {
        console.error("Error fetching patient analytics:", error);
        this.resetData();
        throw error;
      }
    },

    processPatientAnalysisData(data) {

      // Basic summary
      this.patientSummary = data.summary || this.patientSummary;

      // Demographics with proper sorting
      if (data.demographics) {
        this.demographics = {
          ...data.demographics,
          ageGroups: this.sortAgeGroups(data.demographics.ageGroups || []),
          genderDistribution: this.sortGenderDistribution(
            data.demographics.genderDistribution || []
          ),
          locationDistribution: data.demographics.locationDistribution || [],
        };
      } else {
        this.demographics = null;
      }

      // Referral sources
      this.referralSources = data.referralSources || [];

      // Ortho package analysis
      this.orthoPackageAnalysis =
        data.orthoPackageAnalysis || this.orthoPackageAnalysis;

      // Patient segmentation
      this.patientSegmentation = data.patientSegmentation || null;

      // Treatment affinity mapping
      this.treatmentAffinityMapping =
        data.patientSegmentation?.treatmentAffinityMapping || null;

      // New vs existing patients
      this.newVsExistingPatients = data.newVsExistingPatients || null;
    },

    sortAgeGroups(ageGroups) {
      const ageOrder = [
        "0-5 (เด็กเล็ก)",
        "6-11 (เด็กประถม)",
        "12-14 (เด็กมัธยมต้น)",
        "15-17 (เด็กมัธยมปลาย)",
        "18-21 (นักศึกษา)",
        "22-29 (วัยทำงานต้น)",
        "30-39 (วัยทำงานกลาง)",
        "40-49 (วัยทำงานปลาย)",
        "50-59 (วัยใกล้เกษียณ)",
        "60+ (วัยเกษียณ)",
        "Unknown",
      ];

      return ageGroups.sort((a, b) => {
        const indexA = ageOrder.indexOf(a.ageRange);
        const indexB = ageOrder.indexOf(b.ageRange);

        if (indexA === -1 && indexB === -1) return 0;
        if (indexA === -1) return 1;
        if (indexB === -1) return -1;

        return indexA - indexB;
      });
    },

    sortGenderDistribution(genderDistribution) {
      const genderOrder = ["ชาย", "หญิง", "อื่นๆ", "Unknown"];

      return genderDistribution.sort((a, b) => {
        const indexA = genderOrder.indexOf(a.gender);
        const indexB = genderOrder.indexOf(b.gender);

        if (indexA === -1 && indexB === -1) return 0;
        if (indexA === -1) return 1;
        if (indexB === -1) return -1;

        return indexA - indexB;
      });
    },

    resetData() {
      this.analyticsData = null;
      this.patientSummary = {
        totalPatients: 0,
        newPatients: 0,
        returningPatients: 0,
        newPatientPercentage: 0,
      };
      this.demographics = null;
      this.referralSources = [];
      this.orthoPackageAnalysis = {
        totalPatients: 0,
        patientsWithPackages: 0,
        patientsWithoutPackages: 0,
        packageAdoptionRate: 0,
        packageBreakdown: [],
      };
      this.patientSegmentation = null;
      this.treatmentAffinityMapping = null;
      this.newVsExistingPatients = null;
      this.activeSegmentTab = 0;
      this.clearRecommendations();
    },

    forceChartRerender() {
      this.chartKey += 1;
    },

    onSegmentTabChange(tabIndex) {
      this.activeSegmentTab = tabIndex;
      // Force re-render of segment charts when tab changes
      this.$nextTick(() => {
        this.forceChartRerender();
        // Additional delay to ensure DOM is ready for chart rendering
        setTimeout(() => {
          this.forceChartRerender();
        }, 150);
      });
    },

    formatCurrency(value) {
      if (!value) return "0 บาท";
      return new Intl.NumberFormat("th-TH").format(value) + " บาท";
    },

    async fetchRecommendationsData() {
      if (!this.analyticsData) return;

      this.clearRecommendations();
      this.isLoadingRecommendations = true;

      try {
        const response = await this.fetchPatientRecommendationsAction({
          patientAnalysisResult: this.analyticsData,
          clinicUrl: this.$route.params.clinicUrl,
          branchUrl: this.$route.params.branchUrl,
        });

        if (response?.data?.data?.patientRecommendations) {
          const recs = response.data.data.patientRecommendations;
          this.$set(
            this.recommendations,
            "acquisition",
            recs.acquisition || []
          );
          this.$set(this.recommendations, "retention", recs.retention || []);
          this.$set(this.recommendations, "geographic", recs.geographic || []);
          this.$set(this.recommendations, "referral", recs.referral || []);
          this.$set(this.recommendations, "packages", recs.packages || []);
        }
      } catch (error) {
        console.error("Error fetching patient recommendations:", error);
        this.clearRecommendations();
      } finally {
        this.isLoadingRecommendations = false;
      }
    },

    clearRecommendations() {
      this.recommendations = {
        acquisition: [],
        retention: [],
        geographic: [],
        referral: [],
        packages: [],
      };
    },

    getSegmentVariant(index) {
      const variants = ["danger", "warning", "info", "secondary", "success"];
      return variants[index % variants.length];
    },

    getSegmentAgeChartData(segment) {
      if (
        !segment ||
        !segment.demographics ||
        !segment.demographics.ageGroups
      ) {
        return { labels: [], datasets: [] };
      }

      const filteredData = segment.demographics.ageGroups.filter(
        item => item.ageRange !== "Unknown"
      );

      if (filteredData.length === 0) {
        return { labels: [], datasets: [] };
      }

      return {
        labels: filteredData.map(item => item.ageRange),
        datasets: [
          {
            backgroundColor: this.chartColors.slice(0, filteredData.length),
            data: filteredData.map(item => item.count),
          },
        ],
      };
    },

    getSegmentGenderChartData(segment) {
      if (
        !segment ||
        !segment.demographics ||
        !segment.demographics.genderDistribution
      ) {
        return { labels: [], datasets: [] };
      }

      const filteredData = segment.demographics.genderDistribution.filter(
        item => item.gender !== "Unknown"
      );

      if (filteredData.length === 0) {
        return { labels: [], datasets: [] };
      }

      return {
        labels: filteredData.map(item => item.gender),
        datasets: [
          {
            backgroundColor: ["#06B6D4", "#EC4899", "#F59E0B"],
            data: filteredData.map(item => item.count),
          },
        ],
      };
    },

    openContentGenerator(recommendation) {
      this.selectedRecommendation = recommendation;
      this.$refs.contentGenerator.show();
    },
  },
};
</script>

<style scoped>
.chart-container {
  position: relative;
  height: 400px;
}

.chart-container-small {
  position: relative;
  height: 250px;
}

.summary-section {
  background-color: #fff;
  padding: 1.5rem;
  border-radius: 0.5rem;
}

.summary-card {
  height: 100%;
  border: 1px solid #e9ecef;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
}

.summary-content {
  padding: 0.5rem;
}

.text-body-secondary {
  font-size: 0.875rem;
  color: #6c757d;
}

h3 {
  font-size: 1.5rem;
  font-weight: 600;
}

h4 {
  font-size: 1.25rem;
  font-weight: 600;
}

h5 {
  font-size: 1.15rem;
  font-weight: 600;
}

/* Enhanced table styling */
.table {
  font-size: 0.9rem;
}

.table th {
  border-top: none;
  font-weight: 600;
  background-color: #f8f9fa;
}

/* Tab styling */
.nav-pills .nav-link.active {
  background-color: #007bff;
}

/* Loading overlay */
.b-overlay {
  border-radius: 0.375rem;
}

/* Segment details styling */
.segment-details {
  min-height: 300px;
}

/* Fix tab content display */
.tab-content {
  overflow: visible !important;
}

.nav-pills .nav-link {
  margin-bottom: 0.5rem;
}

/* Ensure charts render properly in tabs */
.chart-container-small {
  min-height: 250px;
  width: 100%;
}

/* Better spacing for nested tabs */
.card .nav-tabs,
.card .nav-pills {
  border-bottom: 1px solid #dee2e6;
  margin-bottom: 1rem;
}

.card .tab-content {
  padding: 1rem 0;
}

/* Chart placeholder styling */
.chart-placeholder {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 250px;
  background-color: #f8f9fa;
  border: 1px dashed #dee2e6;
  border-radius: 0.375rem;
  color: #6c757d;
  font-style: italic;
}

/* Force chart containers to maintain their dimensions */
.chart-container,
.chart-container-small {
  overflow: hidden;
  background-color: #fff;
}

/* Ensure segment details have proper spacing */
.segment-details {
  min-height: 400px;
}

/* Better alert styling for segment info */
.alert-info {
  border-left: 4px solid #0ea5e9;
  background-color: #f0f9ff;
  border-color: #bae6fd;
}

/* AI Loading Animation */
.ai-loading {
  display: flex;
  align-items: center;
  gap: 1rem;
  background: linear-gradient(135deg, #e3f2fd, #bbdefb);
  border: none;
  border-radius: 8px;
}

.ai-pulse {
  width: 20px;
  height: 20px;
  background: #1976d2;
  border-radius: 50%;
  animation: pulse 1.5s ease-in-out infinite;
}

@keyframes pulse {
  0% {
    transform: scale(0.95);
    opacity: 0.5;
  }
  50% {
    transform: scale(1.05);
    opacity: 0.8;
  }
  100% {
    transform: scale(0.95);
    opacity: 0.5;
  }
}

/* AI Badge */
.ai-badge {
  position: absolute;
  top: -12px;
  right: 20px;
  background: linear-gradient(135deg, #2196f3, #1976d2);
  color: white;
  padding: 4px 12px;
  border-radius: 16px;
  font-size: 0.85rem;
  font-weight: 500;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  opacity: 0;
  transform: translateY(10px);
  animation: badgeAppear 0.5s ease forwards;
  animation-delay: 0.3s;
}

.ai-icon {
  display: inline-block;
  animation: wave 1.5s ease-in-out infinite;
}

@keyframes wave {
  0%,
  100% {
    transform: rotate(0deg);
  }
  50% {
    transform: rotate(15deg);
  }
}

@keyframes badgeAppear {
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Card Appearance Animation */
.recommendation-card {
  position: relative;
  transform-origin: top center;
  animation: cardAppear 0.6s cubic-bezier(0.34, 1.56, 0.64, 1);
}

@keyframes cardAppear {
  0% {
    opacity: 0;
    transform: scale(0.9) translateY(30px);
  }
  100% {
    opacity: 1;
    transform: scale(1) translateY(0);
  }
}

/* Item Animations */
.recommendation-item {
  animation: slideIn 0.5s cubic-bezier(0.4, 0, 0.2, 1) forwards;
  opacity: 0;
  transform: translateX(-20px);
}

@keyframes slideIn {
  to {
    opacity: 1;
    transform: translateX(0);
  }
}

/* Enhanced Hover Effects */
.recommendation-card {
  transition: all 0.3s ease-in-out;
  box-shadow: 0 4px 6px -1px rgba(33, 150, 243, 0.1),
    0 2px 4px -1px rgba(33, 150, 243, 0.06);
}

.recommendation-card:hover {
  box-shadow: 0 10px 15px -3px rgba(33, 150, 243, 0.2),
    0 4px 6px -2px rgba(33, 150, 243, 0.1);
}

.recommendation-item {
  transition: all 0.3s ease-in-out;
  border-left: 3px solid transparent;
  background: white;
}

.recommendation-item:hover {
  border-left-color: #2196f3;
  background: linear-gradient(to right, rgba(33, 150, 243, 0.05), transparent);
  transform: translateX(5px);
}

.stats-box {
  border-left: 3px solid #1976d2;
  background-color: rgba(33, 150, 243, 0.05);
  transition: all 0.3s ease;
}

.stats-box:hover {
  background-color: rgba(33, 150, 243, 0.1);
  border-left-color: #1565c0;
}

/* Title Animation */
.recommendation-title {
  animation: titleAppear 0.5s ease forwards;
  animation-delay: 0.2s;
  opacity: 0;
  transform: translateY(10px);
}

@keyframes titleAppear {
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Fade transition */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter,
.fade-leave-to {
  opacity: 0;
}
</style>